import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "sonner";
import { memo } from "react";

// Hooks
import { useCountdown, useMidnightCountdown, useScrollReveal } from "@/hooks";

// Config
import { 
  COPA_DATE, 
  TOTAL_VAGAS, 
  VAGAS_OCUPADAS, 
  NOMES_COMPRADORES, 
  CIDADES,
  getWhatsAppLink 
} from "@/config/constants";

// Facebook Pixel
import { 
  trackViewContent, 
  trackPalpiteModalOpen, 
  trackPalpiteSelected, 
  trackWhatsAppClick,
  trackExitIntentShown 
} from "@/utils/facebookPixel";

// Components
import { SocialProof } from "@/components/SocialProof";
import { PalpiteModal } from "@/components/modals/PalpiteModal";
import { ExitModal } from "@/components/modals/ExitModal";

// Sections - Nova ordem conforme especificado
import {
  HeroSection,
  ComoFuncionaSection,
  CaravelaSection,
  SnorkelingSection,
  DesafioHexaSection,
  SorteioSection,
  AvaliacoesSection,
  BlocoValorSection,
  GarantiaSection,
  FAQSection,
  FinalCTASection,
  FooterSection,
  FloatingWhatsApp
} from "@/components/sections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [counter, setCounter] = useState({ current_participants: VAGAS_OCUPADAS, total_slots: TOTAL_VAGAS, percentage: (VAGAS_OCUPADAS/TOTAL_VAGAS)*100 });
  const [testimonials, setTestimonials] = useState([]);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownExitPopup, setHasShownExitPopup] = useState(false);
  
  // Prova social - notificação de compra
  const [socialProof, setSocialProof] = useState(null);
  const [showSocialProof, setShowSocialProof] = useState(false);
  
  // Modal de escolha do palpite
  const [showPalpiteModal, setShowPalpiteModal] = useState(false);
  
  // Countdown até meia-noite (para promoção - APENAS no bloco Hexa)
  const timeLeft = useMidnightCountdown();
  
  // Countdown até a Copa
  const copaCountdown = useCountdown(COPA_DATE);

  // Scroll reveal refs
  const [comoFuncionaRef, comoFuncionaVisible] = useScrollReveal();
  const [caravelaRef, caravelaVisible] = useScrollReveal();
  const [snorkelingRef, snorkelingVisible] = useScrollReveal();
  const [hexaRef, hexaVisible] = useScrollReveal();
  const [sorteioRef, sorteioVisible] = useScrollReveal();
  const [avaliacoesRef, avaliacoesVisible] = useScrollReveal();
  const [valorRef, valorVisible] = useScrollReveal();
  const [garantiaRef, garantiaVisible] = useScrollReveal();
  const [faqRef, faqVisible] = useScrollReveal();
  const [finalRef, finalVisible] = useScrollReveal();
  
  // Loading state for checkout
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Fetch testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  // Facebook Pixel - Track page view
  useEffect(() => {
    trackViewContent();
  }, []);

  // Prova social aleatória
  useEffect(() => {
    const showRandomSocialProof = () => {
      const nome = NOMES_COMPRADORES[Math.floor(Math.random() * NOMES_COMPRADORES.length)];
      const cidade = CIDADES[Math.floor(Math.random() * CIDADES.length)];
      const minutos = Math.floor(Math.random() * 30) + 1;
      
      setSocialProof({
        nome,
        cidade,
        tempo: `${minutos} minuto${minutos > 1 ? 's' : ''} atrás`
      });
      setShowSocialProof(true);
      
      setTimeout(() => setShowSocialProof(false), 5000);
    };

    // Primeira notificação após 15s
    const timeout = setTimeout(showRandomSocialProof, 15000);
    
    // Repetir a cada 30-60s
    const interval = setInterval(() => {
      showRandomSocialProof();
    }, Math.random() * 30000 + 30000);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  // Exit intent
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShownExitPopup) {
        setShowExitPopup(true);
        setHasShownExitPopup(true);
        trackExitIntentShown();
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [hasShownExitPopup]);

  // Handle palpite modal
  const handleOpenPalpiteModal = () => {
    setShowPalpiteModal(true);
    trackPalpiteModalOpen();
  };

  // Handle checkout - vai direto pro Ticto
  const handleCheckout = (palpite) => {
    trackPalpiteSelected(palpite);
    // Redireciona para o checkout do Ticto
    window.location.href = "https://checkout.ticto.app/O398BFF13";
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Toaster position="top-center" richColors />

      {/* PROVA SOCIAL - Notificação de compra */}
      <SocialProof showSocialProof={showSocialProof} socialProof={socialProof} />

      {/* Modal de Escolha do Palpite */}
      <PalpiteModal 
        open={showPalpiteModal} 
        onOpenChange={setShowPalpiteModal}
        onCheckout={handleCheckout}
      />

      {/* Exit Intent Modal */}
      <ExitModal 
        open={showExitPopup}
        onOpenChange={setShowExitPopup}
        onCheckout={() => {
          setShowExitPopup(false);
          handleOpenPalpiteModal();
        }}
      />

      {/* ============================================
          SEÇÃO 1: HERO (TOPO)
          ============================================ */}
      <HeroSection 
        timeLeft={timeLeft}
        onOpenPalpiteModal={handleOpenPalpiteModal}
      />

      {/* ============================================
          SEÇÃO 2: COMO FUNCIONA O PASSEIO (5 passos)
          ============================================ */}
      <ComoFuncionaSection 
        sectionRef={comoFuncionaRef}
        isVisible={comoFuncionaVisible}
      />

      {/* ============================================
          SEÇÃO 3: A CARAVELA (diferencial único)
          ============================================ */}
      <CaravelaSection 
        sectionRef={caravelaRef}
        isVisible={caravelaVisible}
      />

      {/* ============================================
          SEÇÃO 4: NÃO SABE NADAR? SEM PROBLEMAS
          ============================================ */}
      <SnorkelingSection 
        sectionRef={snorkelingRef}
        isVisible={snorkelingVisible}
      />

      {/* ============================================
          SEÇÃO 5: O DESAFIO DO HEXA (camada sazonal - removível)
          ============================================ */}
      <DesafioHexaSection 
        sectionRef={hexaRef}
        isVisible={hexaVisible}
        timeLeft={timeLeft}
        onOpenPalpiteModal={handleOpenPalpiteModal}
      />

      {/* ============================================
          SEÇÃO 6: SORTEIO EM TODO PASSEIO
          ============================================ */}
      <SorteioSection 
        sectionRef={sorteioRef}
        isVisible={sorteioVisible}
      />

      {/* ============================================
          SEÇÃO 7: PROVA SOCIAL (Avaliações)
          ============================================ */}
      <AvaliacoesSection 
        sectionRef={avaliacoesRef}
        isVisible={avaliacoesVisible}
        testimonials={testimonials}
      />

      {/* ============================================
          SEÇÃO 8: STACK DE VALOR (ancoragem)
          ============================================ */}
      <BlocoValorSection 
        sectionRef={valorRef}
        isVisible={valorVisible}
        onOpenPalpiteModal={handleOpenPalpiteModal}
      />

      {/* ============================================
          SEÇÃO 9: GARANTIA 30 DIAS
          ============================================ */}
      <GarantiaSection 
        sectionRef={garantiaRef}
        isVisible={garantiaVisible}
      />

      {/* ============================================
          SEÇÃO 10: FAQ
          ============================================ */}
      <FAQSection 
        sectionRef={faqRef}
        isVisible={faqVisible}
      />

      {/* ============================================
          SEÇÃO 11: CTA FINAL + RODAPÉ
          ============================================ */}
      <FinalCTASection 
        sectionRef={finalRef}
        isVisible={finalVisible}
        onOpenPalpiteModal={handleOpenPalpiteModal}
      />

      <FooterSection />

      {/* WhatsApp Flutuante - Apenas para dúvidas */}
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
